import { useCallback, useEffect, useRef, useState } from 'react';
import type { GymPhoto } from '@atomfit/shared';
import { gymPhotosApi } from '../../api/gym-photos.api.js';
import { translate as t } from '../../i18n/index.js';

type UploadItem = {
  id: number;
  file: File;
  progress: number;
  status: 'queued' | 'uploading' | 'done' | 'error';
  error?: string;
};
const accepted = '.jpg,.jpeg,.png,.webp,.avif,.tif,.tiff,.gif,.svg';
function errorText(error: unknown) {
  const status = (error as { response?: { status?: number } }).response?.status;
  return t(
    status === 429
      ? 'gymPhotos.busy'
      : status === 422
        ? 'gymPhotos.invalid'
        : status === 409
          ? 'gymPhotos.conflict'
          : 'gymPhotos.actionError',
  );
}
function AdminThumbnail({
  photo,
  tenantSlug,
}: {
  photo: GymPhoto;
  tenantSlug: string | undefined;
}) {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = '';
    setFailed(false);
    setUrl('');
    gymPhotosApi
      .image(photo.thumbnailUrl, tenantSlug, controller.signal)
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(data);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photo.thumbnailUrl, tenantSlug, attempt]);
  if (failed)
    return (
      <button type="button" onClick={() => setAttempt((n) => n + 1)}>
        {t('gymPhotos.retry')}
      </button>
    );
  return url ? (
    <img src={url} alt={t('gymPhotos.photoNumber', { number: photo.position + 1 })} />
  ) : (
    <span role="status">{t('gymPhotos.loading')}</span>
  );
}

export function GymPhotosAdmin({
  gymId,
  tenantSlug,
}: {
  gymId?: string;
  tenantSlug?: string | undefined;
}) {
  const [photos, setPhotos] = useState<GymPhoto[]>([]);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [loading, setLoading] = useState(Boolean(gymId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const controller = useRef(new AbortController());
  const sequence = useRef(0);
  const working = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const reload = useCallback(async () => {
    if (!gymId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await gymPhotosApi.adminList(gymId, tenantSlug, controller.current.signal);
      setPhotos(data);
    } catch {
      if (!controller.current.signal.aborted) setError(t('gymPhotos.loadError'));
    } finally {
      if (!controller.current.signal.aborted) setLoading(false);
    }
  }, [gymId, tenantSlug]);
  useEffect(() => {
    controller.current = new AbortController();
    void reload();
    return () => controller.current.abort();
  }, [reload]);
  const patchItem = (id: number, patch: Partial<UploadItem>) =>
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  async function upload(batch: UploadItem[]) {
    if (!gymId || working.current) return;
    working.current = true;
    setBusy(true);
    setError('');
    try {
      for (const item of batch) {
        if (controller.current.signal.aborted) break;
        if (
          item.file.size > 20 * 1024 * 1024 ||
          !/\.(jpe?g|png|webp|avif|tiff?|gif|svg)$/i.test(item.file.name)
        ) {
          patchItem(item.id, { status: 'error', error: t('gymPhotos.invalid') });
          continue;
        }
        patchItem(item.id, { status: 'uploading', progress: 0 });
        try {
          const { data } = await gymPhotosApi.upload(
            gymId,
            item.file,
            tenantSlug,
            (progress) => patchItem(item.id, { progress }),
            controller.current.signal,
          );
          if (controller.current.signal.aborted) break;
          setPhotos((current) => [...current, data]);
          patchItem(item.id, { status: 'done', progress: 100 });
        } catch (err) {
          if (controller.current.signal.aborted) break;
          patchItem(item.id, { status: 'error', error: errorText(err) });
        }
      }
    } finally {
      working.current = false;
      if (!controller.current.signal.aborted) setBusy(false);
    }
  }
  function addFiles(files: FileList | File[]) {
    if (busy || loading || !gymId) return;
    if (photos.length + files.length > 50) {
      setError(t('gymPhotos.limit'));
      return;
    }
    const batch: UploadItem[] = Array.from(files).map((file) => ({
      id: ++sequence.current,
      file,
      progress: 0,
      status: 'queued',
    }));
    setItems((current) => [...current.filter((item) => item.status !== 'done'), ...batch]);
    void upload(batch);
  }
  async function move(index: number, direction: number) {
    if (!gymId || busy) return;
    const ids = photos.map((photo) => photo.id);
    const other = index + direction;
    if (other < 0 || other >= ids.length) return;
    [ids[index], ids[other]] = [ids[other]!, ids[index]!];
    setBusy(true);
    setError('');
    try {
      const { data } = await gymPhotosApi.order(gymId, ids, tenantSlug);
      setPhotos(data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    if (!gymId || !deleteId || busy) return;
    setBusy(true);
    setError('');
    try {
      await gymPhotosApi.delete(gymId, deleteId, tenantSlug);
      setPhotos((current) => current.filter((photo) => photo.id !== deleteId));
      setDeleteId(null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="af-gym-photos-admin" aria-label={t('gymPhotos.title')}>
      <h3>{t('gymPhotos.title')}</h3>
      {!gymId ? (
        <p>{t('gymPhotos.saveFirst')}</p>
      ) : (
        <>
          <p>{t('gymPhotos.immediate')}</p>
          <p className="af-photo-help">{t('gymPhotos.uploadHelp')}</p>
          <div
            className={`af-photo-dropzone${dragging ? ' is-dragging' : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              if (!busy) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              addFiles(event.dataTransfer.files);
            }}
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={accepted}
              aria-label={t('gymPhotos.upload')}
              disabled={busy || loading}
              onChange={(event) => {
                if (event.target.files) addFiles(event.target.files);
                event.target.value = '';
              }}
            />
            <span>{t('gymPhotos.drop')}</span>
          </div>
          {loading && <p role="status">{t('gymPhotos.loading')}</p>}
          {error && (
            <p role="alert">
              {error}{' '}
              <button type="button" disabled={busy} onClick={() => void reload()}>
                {t('gymPhotos.reload')}
              </button>
            </p>
          )}
          <ul className="af-photo-upload-list" aria-live="polite">
            {items.map((item) => (
              <li key={item.id}>
                <span>{item.file.name}</span>
                {item.status === 'uploading' && (
                  <>
                    <progress value={item.progress} max={100} aria-label={item.file.name} />
                    <span>
                      {t(item.progress >= 100 ? 'gymPhotos.optimizing' : 'gymPhotos.uploading')}
                    </span>
                  </>
                )}
                {item.status === 'queued' && <span>{t('gymPhotos.queued')}</span>}
                {item.status === 'done' && <span>{t('gymPhotos.done')}</span>}
                {item.status === 'error' && (
                  <>
                    <span>{item.error}</span>
                    <button type="button" disabled={busy} onClick={() => void upload([item])}>
                      {t('gymPhotos.retry')}
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
          {!loading && !photos.length && !error && <p>{t('gymPhotos.empty')}</p>}
          <ol className="af-admin-photo-grid">
            {photos.map((photo, index) => (
              <li key={photo.id}>
                <div className="af-admin-photo-thumb">
                  <AdminThumbnail photo={photo} tenantSlug={tenantSlug} />
                </div>
                <strong>
                  {index === 0
                    ? t('gymPhotos.cover')
                    : t('gymPhotos.photoNumber', { number: index + 1 })}
                </strong>
                <div className="af-admin-photo-actions">
                  <button
                    type="button"
                    disabled={busy || index === 0}
                    onClick={() => void move(index, -1)}
                    aria-label={t('gymPhotos.moveEarlier', { number: index + 1 })}
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    disabled={busy || index === photos.length - 1}
                    onClick={() => void move(index, 1)}
                    aria-label={t('gymPhotos.moveLater', { number: index + 1 })}
                  >
                    →
                  </button>
                  <button type="button" disabled={busy} onClick={() => setDeleteId(photo.id)}>
                    {t('gymPhotos.delete')}
                  </button>
                </div>
                {deleteId === photo.id && (
                  <div role="group" aria-label={t('gymPhotos.confirmDelete')}>
                    <p>{t('gymPhotos.confirmDelete')}</p>
                    <button type="button" disabled={busy} onClick={() => void remove()}>
                      {t('gymPhotos.delete')}
                    </button>{' '}
                    <button type="button" disabled={busy} onClick={() => setDeleteId(null)}>
                      {t('gymPhotos.cancel')}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}
